import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { rpc } from '@server/infrastructure/rpc'
import {
  classLangTypeSchema,
  classLessonTimeSchema,
  curriculumTypeSchema,
  type LessonEntity,
  type LessonTicketEntity,
} from '@server/schemas'
import { HttpError } from '@server/shared/entry'
import type { Locale } from '@shared/config/i18n/locales'
import { queryOptions } from '@tanstack/react-query'
import { StatusCode } from 'hono/utils/http-status'
import { z } from 'zod'

import { getNextLessonInfoAdapter } from '../lib'
import {
  getCharacterChatLevelResponseSchema,
  getCharacterChatRecommendLessonRequestSchema,
  getCharacterChatRecommendLessonResponseSchema,
  getCompletedLectureRequestSchema,
  getLectureListRequestSchema,
  getNextCourseRequestSchema,
  getNextLectureInfoErrorResponseSchema,
  getNextLectureInfoResponseSchema,
  getPronunciationListResponseSchema,
  getTutorLessonsBySelectedTopicResponseSchema,
  getTutorLessonTopicsRequestSchema,
  getTutorLessonTopicsResponseSchema,
  LessonDiagnosisReportResponse,
  preStudySkipCountResponseSchema,
  type RequestQueryOptionalOptions,
  type RequestQueryOptions,
} from '../model'
import {
  getCompletedLecture,
  getCompletedLessons,
  getDiagnosisReport,
  getLectureEnterInfo,
  getLectureReplayVideo,
  getLectureRoomInfo,
  getNextCourseAction,
  getPodoPrestudyRoomAction,
  getPodoPrestudyRoomMaterialsAction,
  getReplayEnterInfo,
  getReservedLessons,
} from './lesson.action'

type LessonDetailResponse = {
  levelName: string
  courses: LessonEntity[]
}

type PreStudySkipCount = Extract<z.infer<typeof preStudySkipCountResponseSchema>, { resultCd: 200 }>['data']
type LocalizedCharacterChatRecommendLessonRequest = z.infer<typeof getCharacterChatRecommendLessonRequestSchema> & {
  locale?: Locale
}
type LocalizedTutorLessonTopicsRequest = z.infer<typeof getTutorLessonTopicsRequestSchema> & {
  locale?: Locale
}

export const lessonDetailSearchParamsSchema = z.object({
  langType: classLangTypeSchema,
  lessonTime: classLessonTimeSchema,
  level: z.number(),
  curriculumType: curriculumTypeSchema.extract(['BASIC', 'TRIAL', 'SMART_TALK']),
})

export const trialLessonDetailSearchParamsSchema = z.object({
  langType: classLangTypeSchema,
  lessonTime: z.coerce.number().optional().pipe(classLessonTimeSchema.optional()),
  level: z.coerce.number().optional(),
  classCourseId: z.coerce.number().int().positive().optional(),
  curriculumType: curriculumTypeSchema.extract(['TRIAL', 'TRIAL_1', 'TRIAL_2']).optional(),
})

export const lessonHistorySearchParamsSchema = z.object({
  classCourseId: z.number(),
})

export const lessonEntityQueries = {
  all: ['lesson'],

  // Query Keys
  preStudySkipCountQueryKey: ({ bearerToken }: { bearerToken: string }) => [
    ...lessonEntityQueries.all,
    'preStudySkipCount',
    bearerToken,
  ],
  ticketsQueryKey: ({ bearerToken }: { bearerToken: string }) => [...lessonEntityQueries.all, 'tickets', bearerToken],
  trialLessonDetailQueryKey: ({
    bearerToken,
    locale,
    ...query
  }: RequestQueryOptions<z.infer<typeof trialLessonDetailSearchParamsSchema>> & { locale: Locale }) => [
    ...lessonEntityQueries.all,
    'trialLessonDetail',
    bearerToken,
    query,
    locale,
  ],
  lessonDetailQueryKey: ({
    bearerToken,
    locale,
    ...query
  }: RequestQueryOptions<z.infer<typeof lessonDetailSearchParamsSchema>> & { locale: Locale }) => [
    ...lessonEntityQueries.all,
    'lessonDetail',
    bearerToken,
    query,
    locale,
  ],
  lessonHistoryQueryKey: ({
    bearerToken,
    ...query
  }: RequestQueryOptions<z.infer<typeof lessonHistorySearchParamsSchema>>) => [
    ...lessonEntityQueries.all,
    'lessonHistory',
    bearerToken,
    query,
  ],
  lectureEnterInfoQueryKey: ({
    bearerToken,
    classId,
    useV3,
  }: RequestQueryOptions<{ classId: number }> & { useV3?: boolean }) => [
    ...lessonEntityQueries.all,
    'lectureEnterInfo',
    bearerToken,
    classId,
    useV3 ?? false,
  ],
  lectureRoomInfoQueryKey: ({ bearerToken, classId }: RequestQueryOptions<{ classId: number }>) => [
    ...lessonEntityQueries.all,
    'lectureRoomInfo',
    bearerToken,
    classId,
  ],
  podoPrestudyRoomQueryKey: ({ bearerToken, roomId }: RequestQueryOptions<{ roomId: string }>) => [
    ...lessonEntityQueries.all,
    'podoPrestudyRoom',
    bearerToken,
    roomId,
  ],
  podoPrestudyRoomMaterialsQueryKey: ({
    bearerToken,
    classCourseId,
  }: RequestQueryOptions<{ classCourseId: number | string }>) => [
    ...lessonEntityQueries.all,
    'podoPrestudyRoomMaterials',
    bearerToken,
    String(classCourseId),
  ],
  replayEnterInfoQueryKey: ({ bearerToken, classId }: RequestQueryOptions<{ classId: number }>) => [
    ...lessonEntityQueries.all,
    'replayEnterInfo',
    bearerToken,
    classId,
  ],
  lectureReplayVideoQueryKey: ({ bearerToken, classId }: RequestQueryOptions<{ classId: number }>) => [
    ...lessonEntityQueries.all,
    'lectureReplayVideo',
    bearerToken,
    classId,
  ],
  getDiagnosisReportQueryKey: ({ bearerToken, classId }: RequestQueryOptions<{ classId: number }>) => [
    ...lessonEntityQueries.all,
    'getDiagnosisReport',
    bearerToken,
    classId,
  ],
  getCompletedLectureQueryKey: ({
    bearerToken,
    classId,
  }: RequestQueryOptions<z.infer<typeof getCompletedLectureRequestSchema>>) => [
    ...lessonEntityQueries.all,
    'getCompletedLecture',
    bearerToken,
    classId,
  ],
  getReservedLessonsQueryKey: ({ bearerToken, locale = 'ko' }: RequestQueryOptionalOptions & { locale?: Locale }) => [
    ...lessonEntityQueries.all,
    'getReservedLessons',
    bearerToken,
    locale,
  ],
  getCompletedLessonsQueryKey: ({
    bearerToken,
    locale = 'ko',
    ...request
  }: RequestQueryOptionalOptions<z.infer<typeof getLectureListRequestSchema>> & { locale?: Locale }) => [
    ...lessonEntityQueries.all,
    'getCompletedLessons',
    bearerToken,
    request.invoice_status,
    locale,
  ],
  getAiCharacterChatRecommendLessonQueryKey: ({
    bearerToken,
    ...requestDto
  }: RequestQueryOptionalOptions<LocalizedCharacterChatRecommendLessonRequest>) => [
    ...lessonEntityQueries.all,
    'getAiCharacterChatRecommendLesson',
    bearerToken,
    requestDto,
  ],
  getNextLessonsInfoQueryKey: ({ bearerToken }: RequestQueryOptionalOptions) => [
    ...lessonEntityQueries.all,
    'getNextLessonsInfo',
    bearerToken,
  ],
  getTutorLessonsBySelectedTopicQueryKey: ({ bearerToken }: RequestQueryOptionalOptions) => [
    ...lessonEntityQueries.all,
    'getTutorLessonsBySelectedTopic',
    bearerToken,
  ],
  getNextCourseInfoQueryKey: ({
    bearerToken,
    ...request
  }: RequestQueryOptionalOptions<z.infer<typeof getNextCourseRequestSchema>>) => [
    ...lessonEntityQueries.all,
    'getNextCourseInfo',
    bearerToken,
    request,
  ],
  getCharacterChatLevelQueryKey: ({ bearerToken }: RequestQueryOptionalOptions) => [
    ...lessonEntityQueries.all,
    'getCharacterChatLevel',
    bearerToken,
  ],
  getTutorLessonTopicsQueryKey: ({
    bearerToken,
    ...requestDto
  }: RequestQueryOptionalOptions<LocalizedTutorLessonTopicsRequest>) => [
    ...lessonEntityQueries.all,
    'getTutorLessonTopics',
    bearerToken,
    requestDto,
  ],
  getPronunciationListQueryKey: ({ bearerToken }: RequestQueryOptionalOptions) => [
    ...lessonEntityQueries.all,
    'getPronunciationList',
    bearerToken,
  ],

  // Query Functions
  preStudySkipCount: ({ bearerToken }: { bearerToken: string }) =>
    queryOptions({
      queryKey: lessonEntityQueries.preStudySkipCountQueryKey({ bearerToken }),
      queryFn: async (): Promise<PreStudySkipCount> => {
        const response = await podoFetcher<z.infer<typeof preStudySkipCountResponseSchema>>({
          method: 'GET',
          url: '/api/v1/user/podo/getPrestudySkipCount',
          header: { Authorization: `Bearer ${bearerToken}` },
        })

        const parsedResponse = preStudySkipCountResponseSchema.parse(response)

        if (parsedResponse.resultCd !== 200) {
          throw new HttpError(parsedResponse.resultCd as StatusCode, {
            code: parsedResponse.resultCdName,
            message: parsedResponse.message,
          })
        }

        return parsedResponse.data
      },
    }),
  tickets: ({ bearerToken }: { bearerToken: string }) =>
    queryOptions({
      queryKey: lessonEntityQueries.ticketsQueryKey({ bearerToken }),
      queryFn: async () => {
        const response = await rpc.api.v1.lesson.tickets.$get(
          {},
          {
            headers: {
              Authorization: `Bearer ${bearerToken}`,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch lesson tickets')
        }

        return response.json() as Promise<LessonTicketEntity[]>
      },
    }),
  trialLessonDetail: ({
    bearerToken,
    locale,
    ...query
  }: RequestQueryOptions<z.infer<typeof trialLessonDetailSearchParamsSchema>> & { locale: Locale }) =>
    queryOptions({
      queryKey: lessonEntityQueries.trialLessonDetailQueryKey({
        bearerToken,
        locale,
        ...query,
      }),
      queryFn: async (): Promise<LessonEntity[]> => {
        const response = await rpc.api.v1.lesson['trial-level-detail'].$get(
          {
            query: { langType: query.langType },
          },
          {
            headers: {
              Authorization: `Bearer ${bearerToken}`,
              'Accept-Language': locale,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch trial lesson detail')
        }

        return response.json() as Promise<LessonEntity[]>
      },
    }),
  lessonDetail: ({
    bearerToken,
    locale,
    ...query
  }: RequestQueryOptions<z.infer<typeof lessonDetailSearchParamsSchema>> & { locale: Locale }) =>
    queryOptions({
      queryKey: lessonEntityQueries.lessonDetailQueryKey({
        bearerToken,
        locale,
        ...query,
      }),
      queryFn: async (): Promise<LessonDetailResponse> => {
        const response = await rpc.api.v1.lesson['level-detail'].$get(
          {
            query: {
              langType: query.langType,
              lessonTime: query.lessonTime.toString(),
              level: query.level.toString(),
              curriculumType: query.curriculumType,
            },
          },
          {
            headers: {
              Authorization: `Bearer ${bearerToken}`,
              'Accept-Language': locale,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch lesson detail')
        }

        return response.json() as Promise<LessonDetailResponse>
      },
    }),
  lessonHistory: ({ bearerToken, ...query }: RequestQueryOptions<z.infer<typeof lessonHistorySearchParamsSchema>>) =>
    queryOptions({
      queryKey: lessonEntityQueries.lessonHistoryQueryKey({
        bearerToken,
        ...query,
      }),
      queryFn: async (): Promise<LessonEntity[]> => {
        const response = await rpc.api.v1.lesson.history[':classCourseId'].$get(
          {
            param: { classCourseId: query.classCourseId.toString() },
          },
          {
            headers: {
              Authorization: `Bearer ${bearerToken}`,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch lesson history')
        }

        return response.json() as Promise<LessonEntity[]>
      },
    }),
  lectureEnterInfo: ({ bearerToken, classId, useV3 }: RequestQueryOptions<{ classId: number }> & { useV3?: boolean }) =>
    queryOptions({
      queryKey: lessonEntityQueries.lectureEnterInfoQueryKey({
        bearerToken,
        classId,
        useV3,
      }),
      queryFn: async () => getLectureEnterInfo({ classId }, bearerToken, { useV3 }),
      staleTime: 0,
    }),
  lectureRoomInfo: ({ bearerToken, classId }: RequestQueryOptions<{ classId: number }>) =>
    queryOptions({
      queryKey: lessonEntityQueries.lectureRoomInfoQueryKey({
        bearerToken,
        classId,
      }),
      queryFn: async () => getLectureRoomInfo({ classId }, bearerToken),
      staleTime: 0,
    }),
  podoPrestudyRoom: ({ bearerToken, roomId }: RequestQueryOptions<{ roomId: string }>) =>
    queryOptions({
      queryKey: lessonEntityQueries.podoPrestudyRoomQueryKey({ bearerToken, roomId }),
      queryFn: async () => getPodoPrestudyRoomAction({ roomId }, bearerToken),
      staleTime: 0,
    }),
  podoPrestudyRoomMaterials: ({
    bearerToken,
    classCourseId,
  }: RequestQueryOptions<{ classCourseId: number | string }>) =>
    queryOptions({
      queryKey: lessonEntityQueries.podoPrestudyRoomMaterialsQueryKey({ bearerToken, classCourseId }),
      queryFn: async () => getPodoPrestudyRoomMaterialsAction({ classCourseId }, bearerToken),
      staleTime: 0,
    }),
  replayEnterInfo: ({ bearerToken, classId }: RequestQueryOptions<{ classId: number }>) =>
    queryOptions({
      queryKey: lessonEntityQueries.replayEnterInfoQueryKey({
        bearerToken,
        classId,
      }),
      queryFn: async () => getReplayEnterInfo({ classId }, bearerToken),
      staleTime: 0,
    }),
  lectureReplayVideo: ({ bearerToken, classId }: RequestQueryOptions<{ classId: number }>) =>
    queryOptions({
      queryKey: lessonEntityQueries.lectureReplayVideoQueryKey({
        bearerToken,
        classId,
      }),
      queryFn: async () => getLectureReplayVideo({ classId }, bearerToken),
      staleTime: 0,
    }),
  getDiagnosisReport: ({ bearerToken, classId }: RequestQueryOptions<{ classId: number }>) =>
    queryOptions({
      queryKey: lessonEntityQueries.getDiagnosisReportQueryKey({
        bearerToken,
        classId,
      }),
      queryFn: async (): Promise<LessonDiagnosisReportResponse> => {
        return await getDiagnosisReport({ classId }, bearerToken)
      },
    }),

  getCompletedLecture: ({
    bearerToken,
    classId,
  }: RequestQueryOptions<z.infer<typeof getCompletedLectureRequestSchema>>) =>
    queryOptions({
      queryKey: lessonEntityQueries.getCompletedLectureQueryKey({
        bearerToken,
        classId,
      }),
      queryFn: async () => {
        return await getCompletedLecture({ classId }, bearerToken)
      },
    }),

  getReservedLessons: ({ bearerToken, locale = 'ko' }: RequestQueryOptionalOptions & { locale?: Locale }) =>
    queryOptions({
      queryKey: lessonEntityQueries.getReservedLessonsQueryKey({ bearerToken, locale }),
      queryFn: async () => await getReservedLessons(bearerToken, locale),
    }),

  getCompletedLessons: ({
    bearerToken,
    locale = 'ko',
    ...request
  }: RequestQueryOptionalOptions<z.infer<typeof getLectureListRequestSchema>> & { locale?: Locale }) =>
    queryOptions({
      queryKey: lessonEntityQueries.getCompletedLessonsQueryKey({
        bearerToken,
        locale,
        ...request,
      }),
      queryFn: async () => {
        return await getCompletedLessons(request, bearerToken, locale)
      },
    }),

  getAiCharacterChatRecommendLesson: ({
    bearerToken,
    ...requestDto
  }: RequestQueryOptionalOptions<LocalizedCharacterChatRecommendLessonRequest>) =>
    queryOptions({
      queryKey: lessonEntityQueries.getAiCharacterChatRecommendLessonQueryKey({
        bearerToken,
        ...requestDto,
      }),
      queryFn: async () => {
        const { locale = 'en', ...request } = requestDto
        const parsedRequestDto = getCharacterChatRecommendLessonRequestSchema.parse(request)
        const response = await podoFetcher<z.infer<typeof getCharacterChatRecommendLessonResponseSchema>>({
          method: 'GET',
          url: `/api/v2/lecture/ai/recommend?${new URLSearchParams(parsedRequestDto).toString()}`,
          header: {
            Authorization: `Bearer ${bearerToken}`,
            'Accept-Language': locale,
          },
        })

        return response
      },
    }),

  getNextLessonsInfo: ({ bearerToken, timeoutMs }: RequestQueryOptionalOptions & { timeoutMs?: number }) =>
    queryOptions({
      queryKey: lessonEntityQueries.getNextLessonsInfoQueryKey({ bearerToken }),
      queryFn: async ({ signal }) => {
        const response = await podoFetcher<
          z.infer<typeof getNextLectureInfoResponseSchema | typeof getNextLectureInfoErrorResponseSchema>
        >(
          {
            method: 'GET',
            url: '/api/v1/lecture/podo/getNextLectureInfo',
            header: {
              Authorization: `Bearer ${bearerToken}`,
            },
          },
          { signal, timeoutMs },
        )
        return response
      },
      select: (response) => getNextLessonInfoAdapter(response).data,
    }),

  getTutorLessonsBySelectedTopic: ({ bearerToken }: RequestQueryOptionalOptions) =>
    queryOptions({
      queryKey: lessonEntityQueries.getTutorLessonsBySelectedTopicQueryKey({
        bearerToken,
      }),
      queryFn: async () => {
        const response = await podoFetcher<z.infer<typeof getTutorLessonsBySelectedTopicResponseSchema>>({
          method: 'GET',
          url: '/api/v2/lecture/ai/next-lecture',
          header: {
            Authorization: `Bearer ${bearerToken}`,
          },
        })

        return getTutorLessonsBySelectedTopicResponseSchema.parse(response).data
      },
    }),

  getNextCourseInfo: ({
    bearerToken,
    ...request
  }: RequestQueryOptionalOptions<z.infer<typeof getNextCourseRequestSchema>>) =>
    queryOptions({
      queryKey: lessonEntityQueries.getNextCourseInfoQueryKey({
        bearerToken,
        ...request,
      }),
      queryFn: async () => await getNextCourseAction(request, bearerToken),
    }),

  getCharacterChatLevel: ({ bearerToken }: RequestQueryOptionalOptions) =>
    queryOptions({
      queryKey: lessonEntityQueries.getCharacterChatLevelQueryKey({
        bearerToken,
      }),
      queryFn: async () => {
        const response = await podoFetcher<z.infer<typeof getCharacterChatLevelResponseSchema>>({
          method: 'GET',
          url: '/api/v2/lecture/ai/level',
          header: {
            Authorization: `Bearer ${bearerToken}`,
          },
        })

        return getCharacterChatLevelResponseSchema._def.shape().data.parse(response.data)
      },
    }),

  getTutorLessonTopics: ({
    bearerToken,
    ...requestDto
  }: RequestQueryOptionalOptions<LocalizedTutorLessonTopicsRequest>) =>
    queryOptions({
      queryKey: lessonEntityQueries.getTutorLessonTopicsQueryKey({
        bearerToken,
        ...requestDto,
      }),
      queryFn: async () => {
        const { locale = 'en', ...request } = requestDto
        const parsedRequestDto = getTutorLessonTopicsRequestSchema.parse(request)
        const response = await podoFetcher<z.infer<typeof getTutorLessonTopicsResponseSchema>>({
          method: 'GET',
          url: `/api/v2/lecture/ai/level/up/lecture-courses?${new URLSearchParams(parsedRequestDto).toString()}`,
          header: {
            Authorization: `Bearer ${bearerToken}`,
            'Accept-Language': locale,
          },
        })

        return response
      },
    }),

  getPronunciationList: ({ bearerToken }: RequestQueryOptionalOptions) =>
    queryOptions({
      queryKey: lessonEntityQueries.getPronunciationListQueryKey({
        bearerToken,
      }),
      queryFn: async () => {
        const { data } = await podoFetcher<z.infer<typeof getPronunciationListResponseSchema>>(
          {
            method: 'GET',
            url: '/api/v1/lecture/podo/getPronunciationList',
            header: {
              Authorization: `Bearer ${bearerToken}`,
            },
          },
          { throwOnErrorResult: true, httpErrorMode: 'legacy' },
        )

        return getPronunciationListResponseSchema.parse(data)
      },
    }),
} as const
